import { Link } from 'react-router-dom';
import {
  LEP_EB_ID,
  COMMAND_CENTER_METRICS,
  ENROLLMENT_STAGES,
  MIGRATION_TYPES,
  IMPLEMENTATION_STEPS,
  CSM_CAPABILITIES,
  SUCCESS_METRICS,
  HEALTH_FACTORS,
  FEEDBACK_TYPES,
  COMMERCIAL_CHECKS,
  SUPPORT_CAPABILITIES,
  COMMUNITY_FEATURES,
  BETA_ANALYTICS,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_KPIS,
  DEMO_ENROLLMENT,
  DEMO_MIGRATIONS,
  DEMO_IMPLEMENTATION,
  DEMO_CSM,
  DEMO_SUCCESS,
  DEMO_COMMERCIAL,
  DEMO_BETA_OPS,
  buildBetaDashboard,
} from '../../enterprisebeta/framework';
import {
  BetaCommandCenterPanel,
  CustomerEnrollmentPanel,
  MigrationCenterPanel,
  ImplementationGuidePanel,
  AICustomerSuccessManagerPanel,
  SuccessTrackingPanel,
  CommercialReadinessPanel,
  BetaOperationsPanel,
} from '../../components/enterprisebeta';
import { Card, KpiCard } from '../../components/ui';

export function EnterpriseBetaPage() {
  const dashboard = buildBetaDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{LEP_EB_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Beta Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Enterprise Beta Program, Customer Success Validation, Commercial Readiness & Scale Preparation —
          final customer validation phase before General Availability.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Enrollment Stages" value={dashboard.enrollment_stages.toString()} />
        <KpiCard label="Migration Types" value={dashboard.migration_types.toString()} />
        <KpiCard label="Commercial Checks" value={dashboard.commercial_checks.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Beta Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <BetaCommandCenterPanel kpis={DEMO_KPIS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Customer Enrollment</h2>
          <p className="mt-1 text-sm text-slate-500">{ENROLLMENT_STAGES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <CustomerEnrollmentPanel pipeline={DEMO_ENROLLMENT} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Migration Center</h2>
          <p className="mt-1 text-sm text-slate-500">{MIGRATION_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <MigrationCenterPanel migrations={DEMO_MIGRATIONS} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Implementation Guide</h2>
        <p className="mt-1 text-sm text-slate-500">{IMPLEMENTATION_STEPS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ImplementationGuidePanel steps={DEMO_IMPLEMENTATION} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Customer Success Manager</h2>
        <p className="mt-1 text-sm text-slate-500">{CSM_CAPABILITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AICustomerSuccessManagerPanel sessions={DEMO_CSM} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Success Tracking</h2>
        <p className="mt-1 text-sm text-slate-500">{SUCCESS_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <SuccessTrackingPanel metrics={DEMO_SUCCESS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Commercial Readiness</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMERCIAL_CHECKS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <CommercialReadinessPanel checks={DEMO_COMMERCIAL} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Beta Operations</h2>
        <p className="mt-1 text-sm text-slate-500">{SUPPORT_CAPABILITIES.join(' · ')} · {COMMUNITY_FEATURES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <BetaOperationsPanel items={DEMO_BETA_OPS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Customer Health</h2>
          <p className="mt-2 text-xs text-slate-500">{HEALTH_FACTORS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Feedback Management</h2>
          <p className="mt-2 text-xs text-slate-500">{FEEDBACK_TYPES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{BETA_ANALYTICS.join(' · ')}</p>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Accessibility</h2>
        <div className="mt-2 flex flex-wrap gap-1">
          {ACCESSIBILITY_STANDARDS.map((s) => (
            <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
          ))}
        </div>
      </section>
    </div>
  );
}
